/**
 * Correcting a registered patient's demographics (DECISIONS.md #43).
 *
 * **The screen the unconscious patient needs.** Somebody brought in without an identity is
 * registered under a reservnummer with the identity marked as an assumption (#40); when they wake
 * up and say who they are, this is where the record is brought into line. Without it the only way
 * to file the name is a second registration, which manufactures exactly the duplicate that phase 0
 * has no merge to resolve.
 *
 * **Nothing is replaced.** The correction is a new version and the one on screen stays in the
 * record (AGENTS.md §3 rule 2), which the form says above the button rather than in a dialogue that
 * gets clicked through — the same choice `PatientRegistrationScreen` makes about being final.
 *
 * **The form opens pre-filled.** A clinician correcting a spelling must not have to retype a birth
 * date somebody else established; see {@link draftFromCurrent}.
 *
 * **The identifier is not on this form.** Correcting a patient into holding a personnummer is the
 * write #40 refuses client-side, because phase 0 must never hold a real one (AGENTS.md §6) — and
 * leaving the number alone is also what keeps the wristband, the labels and yesterday's notes
 * pointing at this patient.
 *
 * **Nothing is saved until Save**, as on the note editor and the registration form and for the same
 * reason (#38): there is no draft table and no `localStorage`, so the form warns before discarding
 * and before the tab closes.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  correctPatient,
  CorrectionRefusedError,
  CorrectionUnavailableError,
  type CorrectedPatient,
  type CorrectionRefusalCode,
  type CorrectionRefusalParams,
} from './correctionApi'
import {
  correctionExplanation,
  correctionFieldHint,
  correctionFieldLabel,
  correctionHeading,
  correctionMode,
  correctionProblemMessage,
  correctionRefusalMessage,
  correctionSaveLabel,
  correctionUnavailableMessage,
  estimatedIdentityHint,
  type CorrectionTranslate,
} from './correctionDisplay'
import {
  correctionProblem,
  draftFromCurrent,
  isDraftDirty,
  toSubmission,
  type CorrectionDraft,
  type CorrectionField,
} from './correctionDraft'
import type { PatientOverview } from './patientOverviewApi'
import { offeredSexLabel, sexNotAnsweredLabel } from './registrationDisplay'
import { MAXIMUM_FIELD_LENGTH, OFFERED_SEXES } from './registrationDraft'

type Phase =
  | { readonly kind: 'editing' }
  /** A write is in flight. The form is read-only and Save cannot be pressed twice. */
  | { readonly kind: 'saving' }
  /**
   * Written. The form is gone, so the same correction cannot be sent a second time.
   *
   * Carries nothing: the written version number is handed to `onCorrected` for the overview to
   * refetch with, and the confirmation this screen shows deliberately does not name it.
   */
  | { readonly kind: 'corrected' }

type Notice =
  | {
      readonly kind: 'refused'
      readonly code: CorrectionRefusalCode | 'UNKNOWN'
      readonly params: CorrectionRefusalParams
    }
  | { readonly kind: 'unavailable' }
  /** Something this side found before sending — same wording as the server's. */
  | { readonly kind: 'problem'; readonly message: string }

/** Which fields are a courtesy to leave empty, for the `(frivilligt)` marker. */
const OPTIONAL_FIELDS: Readonly<Record<CorrectionField, boolean>> = {
  givenName: true,
  // Required unless the identity is marked as assumed, which the hint says. Not marked optional,
  // because for a patient who can be identified it is not.
  familyName: false,
  birthDate: true,
}

function TextField({
  field,
  value,
  disabled,
  autoFocus,
  onChange,
  t,
}: {
  readonly field: CorrectionField
  readonly value: string
  readonly disabled: boolean
  readonly autoFocus: boolean
  readonly onChange: (value: string) => void
  readonly t: CorrectionTranslate
}) {
  const id = `correction-field-${field}`
  const hintId = `${id}-hint`

  return (
    <div className="registration__field">
      <label htmlFor={id}>
        {correctionFieldLabel(field, t)}
        {OPTIONAL_FIELDS[field] && (
          <span className="registration__optional"> ({t('correction:screen.optional')})</span>
        )}
      </label>

      <p id={hintId} className="registration__hint">
        {correctionFieldHint(field, t)}
      </p>

      <input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-describedby={hintId}
        // `type="text"` for the birth date too, for the reason `PatientRegistrationScreen` gives at
        // length: a native date input reads and accepts the browser's locale format, and the record
        // has one date format (AGENTS.md §5).
        maxLength={MAXIMUM_FIELD_LENGTH}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

/**
 * The correction form.
 *
 * The registration form's classes throughout, deliberately: it is the same form with three fields
 * fewer, and a second set of styles for it would be a second thing to keep in step. The one class
 * of its own is on the section, so a stylesheet can tell them apart later without the markup
 * changing.
 *
 * @param patient The identity being corrected, **read on every render rather than copied on
 *   mount**. That is what makes the stale path work: when a correction is refused because somebody
 *   else got there first, the overview above refetches, this prop arrives with the new `versionId`,
 *   and the next Save supersedes the version that is actually current — with what the clinician
 *   typed still in the boxes.
 * @param onCorrected The correction was written. The overview refetches, so the header shows the
 *   name that is now in the record while the confirmation is still up.
 * @param onStale Somebody else corrected this patient. Refetches without blanking the screen; the
 *   form stays mounted and keeps the draft.
 * @param onClose Leaves the form.
 */
export function PatientCorrectionScreen({
  patient,
  onCorrected,
  onStale,
  onClose,
}: {
  readonly patient: PatientOverview
  readonly onCorrected: (corrected: CorrectedPatient) => void
  readonly onStale: () => void
  readonly onClose: () => void
}) {
  const { t } = useTranslation(['common', 'auth', 'patient', 'registration', 'correction'])

  const [phase, setPhase] = useState<Phase>({ kind: 'editing' })
  const [draft, setDraft] = useState<CorrectionDraft>(() => draftFromCurrent(patient))
  const [notice, setNotice] = useState<Notice | null>(null)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)

  /**
   * Whether a `setState` would still be seen.
   *
   * A submit in flight is deliberately **not** aborted on unmount — see `correctPatient`. A write
   * that reached the server happened, and cancelling the request would not undo it, it would only
   * hide the answer.
   */
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  // Derived from the prop, not stored: after a refetch this is what the record now says, so the
  // unsaved-work guard and the no-change check both move with it.
  const original = draftFromCurrent(patient)
  const dirty = isDraftDirty(draft, original)
  const busy = phase.kind === 'saving'

  /**
   * The browser's own "leave this page?" question, while there is something changed.
   *
   * The only guard against a reload or a closed tab, and it is here because #38 means there is
   * nothing to come back to. The wording is the browser's and cannot be set, which is why the
   * on-screen sentence says it too.
   */
  useEffect(() => {
    if (!dirty || phase.kind === 'corrected') {
      return
    }

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()

      // Both, for the reason `NoteEditor` gives: `preventDefault()` alone is the current spec and
      // only Chrome 119 and later honour it, while Firefox and Safari cancel on a non-empty
      // `returnValue`. The value is never displayed, so it is not a catalogue key.
      event.returnValue = 'unsaved'
    }

    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, phase.kind])

  const update = <Field extends keyof CorrectionDraft>(
    field: Field,
    value: CorrectionDraft[Field],
  ) => {
    setDraft((previous) => ({ ...previous, [field]: value }))

    // A refusal describes a form that has since changed. Keeping it on screen while the clinician
    // fixes it would leave them reading an error about something they have already corrected.
    setNotice(null)
  }

  const submit = () => {
    if (busy) {
      return
    }

    // `new Date()` at the moment of the check rather than at render, so a form left open across
    // midnight does not refuse a birth date of today.
    const problem = correctionProblem(draft, original, new Date())

    if (problem !== null) {
      // Checked here rather than by disabling the button, for the reason
      // `PatientRegistrationScreen` gives: a greyed-out button with no explanation is the worse
      // failure in a stressed setting, and the message is the same one the server would have sent.
      setNotice({ kind: 'problem', message: correctionProblemMessage(problem, t) })
      return
    }

    setPhase({ kind: 'saving' })
    setNotice(null)

    const controller = new AbortController()

    // `patient.versionId` read here and not at mount: it is the version this Save is against, and
    // after a stale refusal and a refetch it is a different one.
    correctPatient(
      patient.patientId,
      toSubmission(draft, patient.versionId),
      controller.signal,
    ).then(
      (corrected) => {
        if (!live.current) {
          return
        }
        setPhase({ kind: 'corrected' })
        onCorrected(corrected)
      },
      (cause: unknown) => {
        if (!live.current) {
          return
        }

        // Back to editing with every field untouched, whatever went wrong. The form is the only
        // copy of what the clinician established.
        setPhase({ kind: 'editing' })

        if (cause instanceof CorrectionRefusedError) {
          setNotice({ kind: 'refused', code: cause.code, params: cause.params })

          if (cause.code === 'CORRECTION_VERSION_STALE') {
            // The one refusal the screen acts on by itself: reload the identity so the next Save
            // names the version that is current. The draft is untouched, so what was typed is
            // still there to compare against what the other clinician wrote.
            onStale()
          }
        } else if (cause instanceof CorrectionUnavailableError) {
          setNotice({ kind: 'unavailable' })
        } else {
          setNotice({ kind: 'refused', code: 'UNKNOWN', params: {} })
        }
      },
    )
  }

  const close = () => {
    if (dirty && phase.kind === 'editing') {
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  // From `patient`, so the wording is the record's state and not the checkbox's: clearing the
  // estimated-identity box is the clinician saying who this is, and the heading must not rename the
  // act they are halfway through. See `correctionMode`.
  const mode = correctionMode(patient)
  const heading = correctionHeading(mode, t)

  if (phase.kind === 'corrected') {
    return (
      <section
        className="registration patient-correction patient-correction--corrected"
        aria-labelledby="correction-heading"
      >
        <h3 id="correction-heading">{heading}</h3>

        {/* `status`, not `alert`: it is good news, and it must not interrupt. */}
        <p className="registration__registered" role="status">
          {t('correction:screen.savedOk')}
        </p>

        <button type="button" onClick={onClose}>
          {t('correction:screen.openPatient')}
        </button>
      </section>
    )
  }

  return (
    <section className="registration patient-correction" aria-labelledby="correction-heading">
      <header className="registration__header">
        <h3 id="correction-heading">{heading}</h3>

        <p className="registration__explains">{correctionExplanation(mode, t)}</p>

        {/* Said before the fields, because it is the question a clinician will otherwise ask
            halfway through — whether the number on the band still works. The append-only property
            is said too, but further down, next to the button that makes it permanent. */}
        <p className="registration__search-first">{t('correction:screen.identifierUnchanged')}</p>
      </header>

      <TextField
        field="givenName"
        value={draft.givenName}
        disabled={busy}
        // The cursor starts on the given name, which for the case this screen exists for — a
        // patient who has just said who they are — is the first empty box.
        autoFocus
        onChange={(value) => update('givenName', value)}
        t={t}
      />

      <TextField
        field="familyName"
        value={draft.familyName}
        disabled={busy}
        autoFocus={false}
        onChange={(value) => update('familyName', value)}
        t={t}
      />

      <TextField
        field="birthDate"
        value={draft.birthDate}
        disabled={busy}
        autoFocus={false}
        onChange={(value) => update('birthDate', value)}
        t={t}
      />

      <div className="registration__field">
        <label htmlFor="correction-field-sex">
          {t('registration:field.sex')}
          <span className="registration__optional"> ({t('correction:screen.optional')})</span>
        </label>

        <select
          id="correction-field-sex"
          value={draft.sex}
          disabled={busy}
          onChange={(event) => update('sex', event.target.value)}
        >
          {/* The empty option, and it is not the same as `unknown`: nobody has answered, versus
              somebody established that it is not known. The record keeps them apart, so the form
              must too — and a correction must be able to leave a null column null. */}
          <option value="">{sexNotAnsweredLabel(t)}</option>

          {OFFERED_SEXES.map((sex) => (
            <option key={sex} value={sex}>
              {offeredSexLabel(sex, t)}
            </option>
          ))}
        </select>
      </div>

      {/* The control #43 turns on. The flag is what the clinician asserts, never what the form
          infers from the name boxes being full — see `CorrectionSubmission.isEstimatedIdentity`. */}
      <div className="registration__field registration__field--checkbox">
        <label htmlFor="correction-field-estimated">
          <input
            id="correction-field-estimated"
            type="checkbox"
            checked={draft.isEstimatedIdentity}
            disabled={busy}
            aria-describedby="correction-field-estimated-hint"
            onChange={(event) => update('isEstimatedIdentity', event.target.checked)}
          />{' '}
          {t('correction:screen.estimatedIdentity')}
        </label>

        <p id="correction-field-estimated-hint" className="registration__hint">
          {estimatedIdentityHint(t)}
        </p>
      </div>

      {/* Announced, because a refusal is the whole message on this form and the clinician's
          attention is on what they have just established about the patient. */}
      <div aria-live="assertive">
        {notice !== null && (
          <div className="registration__notice" role="alert">
            <p>
              {notice.kind === 'problem'
                ? notice.message
                : notice.kind === 'unavailable'
                  ? correctionUnavailableMessage(t)
                  : correctionRefusalMessage(notice.code, notice.params, t)}
            </p>
          </div>
        )}
      </div>

      <footer className="registration__actions">
        {/* The one thing about correcting that a clinician needs to know once, said where the
            decision is made. Not a warning: that both versions stay is the design working. */}
        <p className="registration__final">{t('correction:screen.appendOnly')}</p>

        <button
          type="button"
          className="registration__register"
          // Disabled only while a write is in flight — a second press would write a second
          // version, and there is no `DELETE` (AGENTS.md §3). Never disabled for a problem in the
          // form; see `submit`.
          disabled={busy}
          onClick={submit}
        >
          {busy ? t('correction:screen.saving') : correctionSaveLabel(mode, t)}
        </button>

        <button type="button" disabled={busy} onClick={close}>
          {t('correction:screen.cancel')}
        </button>
      </footer>

      {confirmingDiscard && (
        <div
          className="registration__discard"
          role="alertdialog"
          aria-label={t('correction:screen.discardQuestion')}
        >
          <p>{t('correction:screen.discardQuestion')}</p>

          {/* Keep correcting is first and is the safe one. The destructive button says what it
              does rather than "OK" — this is the click that loses what was typed. */}
          <button type="button" onClick={() => setConfirmingDiscard(false)}>
            {t('correction:screen.discardCancel')}
          </button>
          <button type="button" onClick={onClose}>
            {t('correction:screen.discardConfirm')}
          </button>
        </div>
      )}
    </section>
  )
}
